/**
 * No Gaushala Assigned Page
 *
 * Displayed when a user logs in without having a gaushala assigned
 * Shows a clear message and contact information for admin support
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  AlertCircle,
  User,
  Phone,
  Mail,
  LogOut,
  MessageSquare,
  Building2
} from 'lucide-react';
import { toast } from 'sonner';

/**
 * NoGaushalaAssigned Component
 * Full-screen component that prevents access to gaushala features
 */
export const NoGaushalaAssigned: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  /**
   * Handle logout
   */
  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      toast.error('Logout failed');
      console.error('Logout error:', error);
    }
  };

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-red-50 via-orange-50 to-yellow-50 flex items-center justify-center px-4 py-8">
      {/* Main Card Container */}
      <Card className="w-full max-w-2xl shadow-2xl border-red-200">
        <div className="p-8 lg:p-12">
          {/* Header Section */}
          <div className="flex flex-col items-center text-center mb-8">
            {/* Icon */}
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mb-6">
              <Building2 className="w-10 h-10 text-red-600" />
            </div>

            {/* Title */}
            <h1 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-2">
              No Gaushala Assigned
            </h1>

            {/* Subtitle */}
            <p className="text-gray-600 text-lg">
              Your account doesn't have access to any gaushala yet
            </p>
          </div>

          {/* Alert Section */}
          <Alert className="mb-8 border-yellow-300 bg-yellow-50">
            <AlertCircle className="h-5 w-5 text-yellow-700 flex-shrink-0" />
            <AlertDescription className="text-yellow-800 ml-3">
              <div className="font-semibold mb-1">What does this mean?</div>
              <p className="text-sm">
                Your administrator needs to assign a gaushala to your account before you can access gaushala management features. This is a one-time setup that only the admin can do.
              </p>
            </AlertDescription>
          </Alert>

          {/* Current User Info */}
          <div className="bg-gray-50 rounded-lg p-6 mb-8 border border-gray-200">
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
              <User className="w-5 h-5 mr-2 text-gray-600" />
              Your Account Details
            </h3>

            <div className="space-y-3">
              {/* Name */}
              {user?.name && (
                <div className="flex justify-between items-start">
                  <span className="text-gray-600">Name</span>
                  <span className="text-gray-900 font-medium">{user.name}</span>
                </div>
              )}

              {/* Phone */}
              {user?.phone && (
                <div className="flex justify-between items-start">
                  <span className="text-gray-600">Phone</span>
                  <span className="text-gray-900 font-medium">{user.phone}</span>
                </div>
              )}

              {/* Email */}
              {user?.email && (
                <div className="flex justify-between items-start">
                  <span className="text-gray-600">Email</span>
                  <span className="text-gray-900 font-medium break-all">{user.email}</span>
                </div>
              )}

              {/* Role */}
              {user?.role && (
                <div className="flex justify-between items-start">
                  <span className="text-gray-600">Role</span>
                  <span className="text-gray-900 font-medium capitalize">{user.role}</span>
                </div>
              )}
            </div>
          </div>

          {/* What to Do Section */}
          <div className="bg-blue-50 rounded-lg p-6 mb-8 border border-blue-200">
            <h3 className="font-semibold text-blue-900 mb-4 flex items-center">
              <MessageSquare className="w-5 h-5 mr-2" />
              What to do next
            </h3>

            <ol className="space-y-3 text-sm text-blue-800">
              <li className="flex">
                <span className="mr-3 font-semibold flex-shrink-0">1.</span>
                <span>Share your phone number and name with your administrator</span>
              </li>
              <li className="flex">
                <span className="mr-3 font-semibold flex-shrink-0">2.</span>
                <span>Ask them to assign your account to a gaushala in the admin panel</span>
              </li>
              <li className="flex">
                <span className="mr-3 font-semibold flex-shrink-0">3.</span>
                <span>Log out and log back in to your account</span>
              </li>
              <li className="flex">
                <span className="mr-3 font-semibold flex-shrink-0">4.</span>
                <span>You should then have full access to gaushala management features</span>
              </li>
            </ol>
          </div>

          {/* Contact Section */}
          <div className="bg-green-50 rounded-lg p-6 mb-8 border border-green-200">
            <h3 className="font-semibold text-green-900 mb-4 flex items-center">
              <Phone className="w-5 h-5 mr-2" />
              Admin Contact Information
            </h3>

            <p className="text-sm text-green-800 mb-4">
              If you need immediate assistance, please contact your administrator:
            </p>

            <div className="space-y-2 text-sm">
              <div className="flex items-center text-green-900">
                <Phone className="w-4 h-4 mr-3 flex-shrink-0 text-green-700" />
                <a href="tel:+919999888777" className="hover:underline font-medium">
                  +91 9999 888 777
                </a>
              </div>
              <div className="flex items-center text-green-900">
                <Mail className="w-4 h-4 mr-3 flex-shrink-0 text-green-700" />
                <a href="mailto:admin@saubhagya.com" className="hover:underline font-medium">
                  admin@saubhagya.com
                </a>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col gap-4">
            <Button
              onClick={handleLogout}
              className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold py-3 rounded-lg transition-all"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>

          {/* Footer Note */}
          <p className="text-xs text-gray-500 text-center mt-8">
            For security reasons, we ensure that gaushala access is properly configured by your administrator before granting you full system access.
          </p>
        </div>
      </Card>
    </div>
  );
};

export default NoGaushalaAssigned;
